import React, { useState, useEffect, useRef, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'

import MainContentLayout from '../Layout/MainContentLayout'
import { formatCurrency, formatDate, formatPercentage } from '../shared/utils'
import server from '../../server'
import {
  DateFilterTemplate,
  InputFieldTemplate,
  MinMaxTemplate,
  MultiSelectTemplate,
  SingleDateFilterTemplate,
} from '../shared/FilterTemplates'

// //theme
import 'primereact/resources/themes/lara-light-indigo/theme.css'
// //core
import 'primereact/resources/primereact.min.css'
// //icons
import 'primeicons/primeicons.css'

import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

export default function MoleculesReport() {
  const dt = useRef()

  const [orders, setOrders] = useState()
  const [tableLoading, setTableLoading] = useState(true)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [csvLoading, setcsvLoading] = useState(false)

  const navigate = useNavigate()

  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const [lazyState, setLazyState] = useState(() =>
    getInitialPaginationState(user, {
      sortField: '',
      sortOrder: '',
      filters: {
        'molecule.name': { value: '' },
        'molecule.source': { value: '' },
        drugIdentificationNo: { value: '' },
        totalExtendedPrice: { value: '' },
        totalRefundedAmount: { value: '' },
        averagePa: { value: '' },
      },
    })
  )

  const initLazyState = () => {
    setLazyState((prevState) => ({
      ...prevState,
      first: 0,
      page: 0,
      rows: defaultRows,
      sortField: '',
      sortOrder: '',
      filters: {
        'molecule.name': { value: '' },
        'molecule.source': { value: '' },
        drugIdentificationNo: { value: '' },
        totalExtendedPrice: { value: '' },
        totalRefundedAmount: { value: '' },
        averagePa: { value: '' },
      },
    }))
  }

  const onRowSelect = (event) => {
    const order = orders.find(
      (item) => item.drugIdentificationNo === event.data.drugIdentificationNo
    )
    navigate(`${event.data.drugIdentificationNo}`, { state: order })
  }

  const getData = () => {
    const moleculeName = lazyState?.filters?.['molecule.name'].value
    const moleculeSource = lazyState?.filters?.['molecule.source'].value
    const drugIdentificationNo = lazyState?.filters?.drugIdentificationNo.value

    let params =
      `?page=${lazyState?.page ? lazyState.page + 1 : 1}` +
      `&limit=${lazyState?.rows}`
    if (startDate) params += `&startDate=${formatDate(startDate)}`
    if (endDate) params += `&endDate=${formatDate(endDate)}`
    params +=
      `${
        drugIdentificationNo
          ? '&drugIdentificationNo=' + drugIdentificationNo
          : ''
      }` +
      `${moleculeName ? '&moleculeName=' + moleculeName : ''}` +
      `${
        moleculeSource
          ? moleculeSource.map((item) => `&source=${item.code}`).join('')
          : ''
      }` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setTableLoading(true)

    server
      .get(`/molecules/generate-report/${params}`)
      .then((response) => {
        const molecules = response.data.molecules
        setOrders(molecules)
        setTotalRecords(response.data.totalCount)
        setTableLoading(false)
        // setError(null)
      })
      .catch((error) => {
        setTableLoading(false)
        // setError(error.response.data.message)
      })
  }

  const exportCSV = () => {
    const moleculeName = lazyState?.filters?.['molecule.name'].value
    const moleculeSource = lazyState?.filters?.['molecule.source'].value
    const drugIdentificationNo = lazyState?.filters?.drugIdentificationNo.value

    let params =
      `?page=${lazyState?.page ? lazyState.page + 1 : 1}` +
      `&limit=${lazyState?.rows}`
    if (startDate) params += `&startDate=${formatDate(startDate)}`
    if (endDate) params += `&endDate=${formatDate(endDate)}`
    params +=
      `${
        drugIdentificationNo
          ? '&drugIdentificationNo=' + drugIdentificationNo
          : ''
      }` +
      `${moleculeName ? '&moleculeName=' + moleculeName : ''}` +
      `${
        moleculeSource
          ? moleculeSource.map((item) => `&source=${item.code}`).join('')
          : ''
      }` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`
    setcsvLoading(true)
    server
      .get(`/molecules/export-report/${params}`, {
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'MoleculeReport.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setcsvLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }

  useEffect(() => {
    checkClearBtn()
    return getData()
  }, [lazyState, startDate, endDate])

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const onPage = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  const onSort = (event) => {
    setLazyState(event)
  }

  const onFilter = (event) => {
    setLazyState(event)
  }

  // BODY TEMPLATES
  const totalRefundedAmountBodyTemplate = (molecule) =>
    formatCurrency(molecule.totalRefundedAmount)
  const averagePaBodyTemplate = (molecule) =>
    formatPercentage(molecule.averagePa)
  const totalExtendedPriceBodyTemplate = (molecule) =>
    formatCurrency(molecule.totalExtendedPrice)

  // Filter TEMPLATES
  const moleculeNameFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Molecule name'
    />
  )

  const moleculeSourceFilterElement = (options) => (
    <MultiSelectTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Molecule source'
      options={[
        { name: 'Single', code: 'single' },
        { name: 'Dual', code: 'dual' },
        { name: 'Multi', code: 'multi' },
      ]}
    />
  )

  const dinFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='DIN'
    />
  )

  const checkClearBtn = () => {
    if (lazyState?.sortField || lazyState?.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in lazyState?.filters) {
      if (lazyState?.filters[key].value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  // TOOLBARS
  const topRightToolbarTemplate = () => (
    <>
      <div className='d-flex gap-1 align-items-center'>
        <SingleDateFilterTemplate
          state={startDate}
          stateHandler={(e) => setStartDate(e.value)}
          placeholder='From'
          showIcon={false}
        />
        <SingleDateFilterTemplate
          state={endDate}
          stateHandler={(e) => setEndDate(e.value)}
          placeholder='To'
          disabled={!startDate}
          minDate={startDate}
        />
      </div>
    </>
  )

  const leftToolbarTemplate = () => (
    <Button
      type='button'
      icon='pi pi-filter-slash'
      label='Clear'
      outlined
      onClick={initLazyState}
      disabled={clearBtnDisabled}
    />
  )

  return (
    <MainContentLayout
      title='Molecules Report'
      dt={exportCSV}
      handleExport={true}
      loading={csvLoading}
    >
      <Toolbar
        className='mb-2'
        left={leftToolbarTemplate}
        right={topRightToolbarTemplate}
      />
      <DataTable
        ref={dt}
        lazy
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        totalRecords={totalRecords}
        paginator
        value={orders}
        selectionMode='single'
        selection={selectedOrder}
        onSelectionChange={(e) => setSelectedOrder(e.value)}
        dataKey='drugIdentificationNo'
        onRowSelect={onRowSelect}
        tableStyle={{ minWidth: '50rem' }}
        loading={tableLoading}
        filters={lazyState?.filters}
        sortField={lazyState?.sortField}
        sortOrder={lazyState?.sortOrder}
        rows={lazyState?.rows}
        first={lazyState?.first}
        onPage={onPage}
        onSort={onSort}
        onFilter={onFilter}
        removableSort
        emptyMessage='No molecules found in the selected data range'
      >
        <Column
          field='molecule.name'
          header='Molecule Name'
          filter
          filterElement={moleculeNameFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          field='molecule.source'
          header='Molecule Source'
          filter
          filterElement={moleculeSourceFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          field='drugIdentificationNo'
          header='DIN'
          filter
          filterElement={dinFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          field='totalExtendedPrice'
          header='Total Extended Price'
          body={totalExtendedPriceBodyTemplate}
          sortable
        />
        <Column
          field='totalRefundedAmount'
          header='Order Refunded Amount'
          body={totalRefundedAmountBodyTemplate}
          sortable
        />
        <Column
          field='averagePa'
          header='Average PA Percentage'
          body={averagePaBodyTemplate}
          sortable
        />
      </DataTable>
    </MainContentLayout>
  )
}
